/**
 * "MODELS SIZE" button over the product gallery (blocks/lc-model-size.liquid).
 *
 * The block element in the details column renders nothing; this module mounts a <button aria-expanded> + <p role="status">
 * into the gallery's <slideshow-container> (the positioned box holding only the image) and keeps it in step with the slideshow:
 * `slideshow:select` (SlideshowSelectEvent, bubbles from <slideshow-component>) swaps the text and closes the panel; a variant
 * change makes media-gallery.js replace the whole <media-gallery>, which takes the mount with it — a MutationObserver on the
 * gallery's parent re-mounts. Text = the product's Model sizing entry for the current slide's media id (`data-photo-texts`, a JSON
 * map built from the custom.model_photos metafield) or the block's one-text-for-all setting; alt text is never read (it is for SEO).
 * Escape closes and returns focus to the button.
 * Theme editor: selecting the block opens the panel.
 */
import { SlideshowSelectEvent } from '@theme/events';

class LcModelSize extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {MutationObserver | undefined} */
  #observer;
  /** @type {MutationObserver | undefined} */
  #slidesObserver;
  /** @type {HTMLElement | null} */
  #mount = null;
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {HTMLElement | null} */
  #panel = null;
  #text = '';

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const scope = this.closest('.shopify-section') ?? document;
    scope.addEventListener(SlideshowSelectEvent.eventName, this.#onSlide, { signal });
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });
    }
    this.#attach();
    const column = this.#gallery()?.parentElement;
    if (column) {
      this.#observer = new MutationObserver(() => this.#attach());
      this.#observer.observe(column, { childList: true });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#slidesObserver?.disconnect();
    this.#mount?.remove();
    this.#mount = this.#button = this.#panel = null;
  }

  #gallery() {
    return this.closest('.shopify-section')?.querySelector('media-gallery') ?? null;
  }

  /** Mounts (or re-mounts after a gallery swap) into the current gallery's slideshow-container. */
  #attach() {
    const gallery = this.#gallery();
    const container = gallery?.querySelector('slideshow-component > slideshow-container');
    if (!container) return;
    if (this.#mount?.isConnected && this.#mount.parentElement === container) return;
    this.#mount?.remove();

    const mount = document.createElement('div');
    mount.className = 'lc-ms';
    mount.setAttribute('style', this.getAttribute('style') ?? '');
    const id = `lc-ms-panel-${this.dataset.blockId ?? ''}`;

    const panel = document.createElement('p');
    panel.className = 'lc-ms__panel';
    panel.id = id;
    panel.setAttribute('role', 'status');
    panel.hidden = true;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'lc-ms__btn';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-controls', id);
    button.textContent = this.dataset.label ?? '';
    button.addEventListener('click', () => this.#toggle(), { signal: this.#controller?.signal });

    mount.append(panel, button);
    mount.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape' || panel.hidden) return;
        event.stopPropagation();
        this.#toggle(false);
        button.focus();
      },
      { signal: this.#controller?.signal }
    );

    container.append(mount);
    this.#mount = mount;
    this.#button = button;
    this.#panel = panel;
    this.#update(this.#currentSlide(gallery));
    // lc-variant-picker.js reorders the slides on load (and marks the shown one with aria-hidden) without a
    // slideshow:select, so the first text would stick to whatever slide came first in Horizon's order
    this.#slidesObserver?.disconnect();
    this.#slidesObserver = new MutationObserver(() => this.#update(this.#currentSlide(gallery), true));
    this.#slidesObserver.observe(container, { childList: true, subtree: true, attributeFilter: ['aria-hidden'] });
  }

  /** @param {Element | null} gallery */
  #currentSlide(gallery) {
    const slideshow = /** @type {any} */ (gallery?.querySelector('slideshow-component'));
    if (!slideshow) return null;
    // the slide on screen (aria-hidden="false") first: Horizon's slides[current] is stale right after a reorder
    const shown = slideshow.querySelector('slideshow-slide[aria-hidden="false"]');
    return shown ?? (Array.isArray(slideshow.slides) ? slideshow.slides[slideshow.current] : null) ?? slideshow.querySelector('slideshow-slide');
  }

  /** @param {Element | null} slide */
  #textFor(slide) {
    if (this.dataset.source === 'text') return (this.dataset.text ?? '').trim();
    const id = /** @type {HTMLElement | null} */ (slide?.querySelector('.product-media[data-media-id]'))?.dataset.mediaId;
    return (id && JSON.parse(this.dataset.photoTexts || '{}')[id]) || '';
  }

  /**
   * New slide: swap the text, hide the button when there is none, and close the panel.
   * @param {Element | null} slide
   * @param {boolean} [ifChanged] only when the text differs (DOM mutations fire for unrelated changes too)
   */
  #update(slide, ifChanged) {
    const text = this.#textFor(slide);
    if (!this.#button || (ifChanged && text === this.#text)) return;
    this.#text = text;
    this.#button.hidden = !this.#text;
    this.#toggle(false);
  }

  /** @param {boolean} [open] */
  #toggle(open = this.#panel?.hidden ?? false) {
    if (!this.#panel || !this.#button) return;
    const show = open && Boolean(this.#text);
    this.#panel.textContent = show ? this.#text : '';
    this.#panel.hidden = !show;
    this.#button.setAttribute('aria-expanded', String(show));
  }

  /** @param {Event} event */
  #onSlide = (event) => {
    const detail = /** @type {SlideshowSelectEvent} */ (event).detail;
    if (!(event.target instanceof Element) || !this.#gallery()?.contains(event.target)) return;
    this.#update(detail?.slide ?? null);
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (event.target === this || (event.target instanceof Element && event.target.contains(this))) this.#toggle(true);
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (event.target === this || (event.target instanceof Element && event.target.contains(this))) this.#toggle(false);
  };
}

if (!customElements.get('lc-model-size')) {
  customElements.define('lc-model-size', LcModelSize);
}
